import {
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';

import {
  BehaviorSubject,
  Observable
} from 'rxjs';

import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                           from '../api/api.service';
import { CompanyIntegrationsModel }             from '../companies/models/company.integrations';
import { CompanyService }                       from '../companies/company.service';
import { ConfirmService }                       from '../messages/confirm/confirm.service';
import { HaynesProAddLaborTimesComponent }      from './haynes-pro.add-labor-times.component';
import { HaynesProAdjustmentModel }             from './typings/haynes-pro.adjustment';
import { HaynesProCategoryModel }               from './typings/haynes-pro.category';
import { HaynesProDiagnosticToolComponent }     from './diagnostic-tool/haynes-pro.diagnostic-tool.component';
import { HaynesProDrawingModel }                from './typings/haynes-pro.drawing';
import { HaynesProLaborOptionModel }            from './typings/haynes-pro.labor-option';
import { HaynesProLaborTimeModel }              from './typings/haynes-pro.labor-time';
import { HaynesProLubricantModel }              from './typings/haynes-pro.lubricant';
import { HaynesProPurchaseLookupsComponent }    from './haynes-pro.purchase-lookups.component';
import { HaynesProRecordTypeModel }             from './typings/haynes-pro.record-type';
import { HaynesProRepairManualModel }           from './typings/haynes-pro.repair-manual';
import { HaynesProRepairSubjectModel }          from './typings/haynes-pro.repair-subject';
import { HaynesProServiceIntervalModel }        from './typings/haynes-pro.service-interval';
import { HaynesProSmartPackBulletinGroupModel } from './typings/haynes-pro.smart-pack.bulletin-group';
import { HaynesProSmartPackCategoryModel }      from './typings/haynes-pro.smart-pack.category';
import { HaynesProSmartPackSystemModel }        from './typings/haynes-pro.smart-pack.system';
import { HaynesProStoryModel }                  from './typings/haynes-pro.story';
import { HaynesProTechnicalDataModel }          from './typings/haynes-pro.technical-data';
import { HaynesProVehicleInfoModel }            from './typings/haynes-pro.vehicle-info';
import { HaynesProVehicleMakeModel }            from './typings/haynes-pro.vehicle-make';
import { HaynesProVehicleModelModel }           from './typings/haynes-pro.vehicle-model';
import { HaynesProVehicleSelectionComponent }   from './haynes-pro.vehicle-selection.component';
import { HaynesProVehicleSubnodesModel }        from './typings/haynes-pro.vehicle-sub-nodes';
import { HaynesProVehicleTypeModel }            from './typings/haynes-pro.vehicle-type';
import { IntegrationQuotePricesModel }          from '../integrations/typings/integration.quote-prices';
import { MessagesService }                      from '../messages/messages.service';
import { QuoteModel }                           from '../core/models/quote';
import { SessionService }                       from '../sessions/session.service';
import { STATUSCODES }                          from '../api/api.status-codes.constant';
import { VehicleModel }                         from '../vehicles/models/vehicle';

@Injectable({
  providedIn: 'root'
})
export class HaynesProService {
  private _hiddenCategories   = [ 'Electronic' ];
  private _subscriptionStatus = new BehaviorSubject<boolean>(false);
  private _STATUSCODES        = STATUSCODES;

  public lookupsTitle        = '';
  public subscriptionStatus$ = this._subscriptionStatus.asObservable();
  public title               = '';

  constructor (
    private apiService     : ApiService,
    public companyService  : CompanyService,
    private confirmService : ConfirmService,
    private messagesService: MessagesService,
    private modalService   : NgbModal,
    private sessionService : SessionService,
    public translateService: TranslateService
  ) {
    this.translateService.get('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.LOOKUPS_TITLE')
    .subscribe(( value : string ) => this.lookupsTitle = value);

    this.translateService.get('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.TITLE')
    .subscribe(( value : string ) => this.title = value);
  }

  activateLookups () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.lookupsTitle
    }))
    .then(() => this.apiService.get('/company/add_haynes_pro_lookup'));
  }

  activateSubscription () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company_integration/add_haynes_pro'));
  }

  addLaborTimes ( vehicle : VehicleModel, recordId : string, type : HaynesProRecordTypeModel ) : ng.IPromise<Array<HaynesProLaborTimeModel>> {
    if ( !vehicle.vin && !this.hasMakeAndModelSearchEnabled()) {
      return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.NO_VIN_ERROR'));
    }

    if ( !this.companyService.hasDefaultLaborProduct() ) {
      return Promise.reject(this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.SET_A_DEFAULT_LABOUR_PRODUCT'));
    }

    let variants : Array<HaynesProVehicleInfoModel>;

    return this.getVehicleVariants(vehicle)
    .then(( vehicles : Array<HaynesProVehicleInfoModel> = [] ) => {
      variants = vehicles;

      if (vehicles.length === 1) {
        vehicle.haynes_pro_vehicle_id = vehicles[0].id;

        return this.saveVehicle(vehicle);
      }

      return vehicle.haynes_pro_vehicle_id
        ? Promise.resolve(vehicle)
        : this.selectVehicleVariant(vehicle, vehicles);
    })
    .then(( vehicle : VehicleModel ) => {
      const modal = this.modalService.open(HaynesProAddLaborTimesComponent, {
        windowClass: 'x-lg-modal pull-up'
      });

      modal.componentInstance.recordId    = recordId;
      modal.componentInstance.type        = type;
      modal.componentInstance.variants    = variants;
      modal.componentInstance.vehicle     = vehicle;

      return modal.result;
    });
  }

  addLaborTimesWithNoLabor ( laborOptions : Array<HaynesProLaborOptionModel> ) : Array<HaynesProLaborTimeModel> {
    return laborOptions.flatMap(group =>group.tasks.filter(task => task.labour_time === 0.0));
  }

  buildLaborTimeFromInterval ( interval : HaynesProServiceIntervalModel, laborOptions: Array<HaynesProLaborOptionModel> ) : HaynesProLaborTimeModel {
    return {
      builtFromServiceInterval: true,
      labour_time             : interval.labour_time,
      name                    : interval.period_name,
      subgroup_id             : interval.period_id,
      notes                   : this.getNotesFromLaborOptions(laborOptions)
    };
  }

  checkSubscriptionStatus () : Observable<boolean> {
    return this.subscriptionStatus$;
  }

  confirmLookupPurchase ( quantity : number ) : Promise<any>{
    return this.apiService.get('/purchase_integration_lookups/purchase',[
      true,
      quantity,
      'haynes_pro_lookup'
    ]);
  }

  deactivateLookups () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.REMOVE_INTEGRATION', {
      name: this.lookupsTitle
    }))
    .then(() => this.apiService.get('/company/remove_haynes_pro_lookup'));
  }

  deactivateSubscription () : Promise<boolean> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.REMOVE_INTEGRATION', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company_integration/remove_haynes_pro'));
  }

  filterHiddenCategories ( technicalData : HaynesProTechnicalDataModel ) : Array<HaynesProCategoryModel> {
    return technicalData.categories.map(( category : HaynesProCategoryModel ) =>
      this._hiddenCategories
      .some(title => title.toLowerCase() === category.title.toLowerCase())
        ? { ...category, isHidden: true }
        : category
    );
  }

  getAdjustments ( vehicle : VehicleModel, groupType: string ) : Promise<Array<HaynesProAdjustmentModel>> {
    return this.apiService.get('/haynes_pro/repairs/adjustment_data', [ vehicle.id, groupType ]);
  }

  getCurrentPrice ( quantity : number, prices: IntegrationQuotePricesModel ) : string {
    return quantity >= 1000
      ? prices.low_price
      : ( quantity >= 500
        ? prices.medium_price
        : prices.high_price
      );
  }

  getGlobalVehicle ( id = this.sessionService.base()?.haynes_pro_global_vehicle ) : Promise<VehicleModel> {
    return this.apiService.get('/vehicle', id);
  }

  getLaborOptions ( vehicle : VehicleModel, interval : HaynesProServiceIntervalModel ) : Promise<Array<HaynesProLaborOptionModel>> {
    return this.apiService.get('/haynes_pro/servicing/maintenance_tasks', [ vehicle.id, interval.system_id, interval.period_id ])
    .then(( response : any ) => response.groups);
  }

  getLocations ( vehicle : VehicleModel ) : Promise<Array<any>> {
    return this.apiService.get('/haynes_pro/servicing/location_data', [ vehicle.id ]);
  }

  getLookupQuote () : Promise<QuoteModel> {
    return this.apiService.get('/company/quote_for_haynes_pro_lookup');
  }

  getLookupRequests () : string {
    const company = this.sessionService.company();

    return company?.haynes_pro_lookup_requests;
  }

  getLubricants ( vehicle : VehicleModel, groupType: string ) : Promise<Array<HaynesProLubricantModel>> {
    return this.apiService.get('/haynes_pro/servicing/lubricants_and_capacities', [ vehicle.id, groupType ])
    .then(( response : any ) => response.lubricants);
  }

  getNotesFromLaborOptions ( laborOptions : Array<HaynesProLaborOptionModel> ) : string {
    return laborOptions.flatMap(option => option.tasks)
    .filter(( task : HaynesProLaborTimeModel ) => task.labour_time === 0)
    .map(task => task.name?.trim())
    .join('\n');
  }

  getRequestPrices () : Promise<IntegrationQuotePricesModel> {
    return this.apiService.get('/purchase_integration_lookups/current_lookup_cost/haynes_pro_lookup');
  }

  getPurchaseLookupQuote ( quantity : number ) : Promise<string> {
    return this.apiService.get('/purchase_integration_lookups/quote', [ quantity, 'haynes_pro_lookup' ]);
  }

  getRepairCategories ( vehicle : VehicleModel, subject : HaynesProRepairSubjectModel ) : Promise<Array<HaynesProLaborTimeModel>> {
    return this.apiService.get('/haynes_pro/servicing/repairtime_categories', [ vehicle.id, subject.name ]);
  }

  getRepairSubGroups ( vehicle : VehicleModel, subject : HaynesProRepairSubjectModel, category : HaynesProLaborTimeModel ) : Promise<Array<HaynesProLaborTimeModel>> {
    return this.apiService.get('/haynes_pro/servicing/repairtime_subgroups', [ vehicle.id, subject.name, category.subgroup_id ]);
  }

  getRepairSubjects ( vehicle : VehicleModel ) : Promise<Array<HaynesProRepairSubjectModel>> {
    return this.apiService.get('/haynes_pro/servicing/get_vehicle_technical_subjects', vehicle.id)
    .then(( response : any ) => response ? response?.subjects : []);
  }

  getRepairStories ( vehicle : VehicleModel, groupType: string ) : Promise<Array<HaynesProStoryModel>> {
    return this.apiService.get('/haynes_pro/repairs/manuals_overview_by_group', [ vehicle.id, groupType ])
      .then(response => Array.isArray(response)
        ? response
        : []);
  }

  getRepairManuals ( vehicle : VehicleModel, storyId: string ) : Promise<Array<HaynesProRepairManualModel>> {
    return this.apiService.get('/haynes_pro/repairs/manual_by_story_id', [ vehicle.id, storyId ]);
  }

  getServiceIntervals ( vehicle : VehicleModel ) : Promise<Array<HaynesProServiceIntervalModel>> {
    return this.apiService.get('/haynes_pro/servicing/maintenance_data', vehicle.id, null, { verbose: true })
    .then(( response : HttpResponse<Array<HaynesProServiceIntervalModel>> ) => response.body)
    .catch(( err : HttpErrorResponse ) => {
      if (this.hasInsufficientRequests(err)) {
        this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

        return this.purchaseLookups()
        .then(() => this.getServiceIntervals(vehicle));
      }

      return Promise.reject(err.error ? err.error : err);
    });
  }

  getSmartPackBulletins ( identifier : string, vehicle : VehicleModel, category : HaynesProSmartPackCategoryModel, system : HaynesProSmartPackSystemModel ) : Promise<Array<HaynesProSmartPackBulletinGroupModel>> {
    let url : string;

    identifier = identifier === 'ALL'
      ? category?.categoryType
      : identifier;

    switch (identifier) {
      case 'CASES':
        url = '/haynes_pro/smartpack/smartcase_bulletins';
        break;
      case 'RECALLS':
        url = '/haynes_pro/smartpack/recall_bulletins';
        break;
      case 'TSB':
        url = '/haynes_pro/smartpack/tsb_bulletins';
        break;
    }

    if ( !url ) {
      return Promise.reject('Unsupported category type');
    }

    return this.apiService.get(url, [ vehicle.id, system.system_id ]);
  }

  getSmartPack ( identifier : string, vehicle : VehicleModel, group : string ) : Promise<Array<HaynesProSmartPackCategoryModel>> {
    let params : any;
    let url    : string;

    switch (identifier) {
      case 'CASES':
        params = [ vehicle.id, group ];
        url    = '/haynes_pro/smartpack/cases_systems';
        break;
      case 'RECALLS':
        params = [ vehicle.id ];
        url    = '/haynes_pro/smartpack/recall_systems';
        break;
      case 'TSB':
        params = [ vehicle.id, group ];
        url    = '/haynes_pro/smartpack/tsb_systems';
        break;
      default:
        params = [ vehicle.id, group ];
        url    = '/haynes_pro/smartpack/tbs_cases_recalls_systems';
        break;
    }

    return this.apiService.get(url, params);
  }

  getSubscriptionLookupRequests () : string {
    const company = this.sessionService.company();

    return company?.haynes_pro_subscription_lookup_requests;
  }

  getTechnicalDrawings ( vehicle : VehicleModel, groupType: string ) : Promise<Array<HaynesProDrawingModel>> {
    return this.apiService.get('/haynes_pro/repairs/technical_drawings', [ vehicle.id, groupType ]);
  }

  getVehicleTypes () : Array<HaynesProVehicleTypeModel> {
    return [
      {
        description   : 'cars',
        icon          : 'car',
        language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.VEHICLE_TYPES.CARS',
        name          : 'cars',
        status        : false
      },
      {
        description   : 'motorcycles',
        icon          : 'motorcycle',
        language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.VEHICLE_TYPES.MOTORCYCLES',
        name          : 'motorcycles',
        status        : false
      },
      {
        description   : 'trucks',
        icon          : 'truck',
        language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.VEHICLE_TYPES.TRUCKS',
        name          : 'trucks',
        status        : false
      }
    ];
  }

  getTruckTypes () : Array<HaynesProVehicleTypeModel> {
    return [
      {
        description   : 'lcv',
        language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.TRUCK_TYPES.LCV',
        name          : 'lcv',
        status        : false
      },
      // To be uncommented later when trailer data is ready
      // {
      //   description   : 'trailers',
      //   language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.TRUCK_TYPES.TRAILERS',
      //   name          : 'trailers',
      //   status        : false
      // },
      {
        description   : 'trucks',
        language_label: 'COMPANY_SPACE.INTEGRATIONS.HAYNES_PRO.TRUCK_TYPES.TRUCKS',
        name          : 'trucks',
        status        : false
      }
    ];
  }

  getVehicleMakes ( vehicle : VehicleModel, type : string, subType = '*'  ) : Promise<Array<HaynesProVehicleMakeModel>> {
    return this.apiService.get('/haynes_pro/search/get_makes', [
        vehicle.id,
        type,
        subType
      ]);
  }

  getVehicleModels ( vehicle : VehicleModel, type = '*', subType = '*', make : HaynesProVehicleMakeModel ) : Promise<Array<HaynesProVehicleSubnodesModel>> {
    return this.apiService.get('/haynes_pro/search/get_models', [
        vehicle.id,
        type,
        subType,
        make.id
      ])
      .then(( model : HaynesProVehicleModelModel ) => model.sub_nodes);
  }

  getVehicleTechnicalData ( vehicle : VehicleModel ) : Promise<HaynesProTechnicalDataModel> {
    return this.apiService.get('/haynes_pro/servicing/get_vehicle_technical_overview', [ vehicle.id ])
    .then(( technicalData : HaynesProTechnicalDataModel ) => {
      return {
        ...technicalData,
        categories: this.filterHiddenCategories(technicalData)
      };
    })
    .catch(( err: HttpErrorResponse ) => {
      if (this.hasInsufficientRequests(err)) {
        this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

        return this.purchaseLookups()
        .then(() => this.getVehicleTechnicalData(vehicle));
      }

      return Promise.reject(err.error ? err.error : err);
    });
  }

  getVehicleVariants ( vehicle : VehicleModel ) : Promise<Array<HaynesProVehicleInfoModel>> {
    return Promise.resolve()
    .then(() => {
      return vehicle.haynes_pro_model_id
      ? this.apiService.get('/haynes_pro/search/get_types', [
        vehicle.id,
        vehicle.haynes_pro_model_id
      ])
      .then(( vehicleDetails : HaynesProVehicleModelModel ) => vehicleDetails.sub_nodes)
      : this.apiService.get('/haynes_pro/servicing/get_vin_vehicle_list', [
        vehicle.id,
        true
      ]);
    })
    .catch(( err : HttpErrorResponse ) => {
      if (this.hasInsufficientRequests(err)) {
        this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

        return this.purchaseLookups()
        .then(() => this.getVehicleVariants(vehicle));
      }

      if ( this.hasMakeAndModelSearchEnabled() ){
        this.messagesService.logErrorSilently(err);

        return Promise.resolve([]);
      }

      else {
        return Promise.reject(err);
      }


    });
  }

  getVehicleVariantsBySearch ( vehicle : VehicleModel, model : HaynesProVehicleModelModel ) : Promise<HaynesProVehicleModelModel> {
    return this.apiService.get('/haynes_pro/search/get_types', [
        vehicle.id,
        model.id
      ])
      .catch(( err: HttpErrorResponse ) => {
        if (this.hasInsufficientRequests(err)) {
          this.messagesService.error(this.translateService.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'));

          return this.purchaseLookups()
          .then(() => this.getVehicleVariants(vehicle));
        }

        return Promise.reject(err.error ? err.error : err);
      });
  }

  getWarningLights ( vehicle : VehicleModel ) : Promise<Array<any>> {
    return this.apiService.get('/haynes_pro/repairs/warning_lights', [ vehicle.id ]);
  }

  hasInsufficientRequests ( err : any ) : boolean {
    return err?.status === this._STATUSCODES.paymentRequired
      && err?.error.type === 'Q';
  }

  hasMakeAndModelSearchEnabled () : boolean {
    return this.companyService.country(['australia','newZealand']);
  }

  hasUnlimitedLookups () : boolean {
    return this.companyService.country([
      'australia',
      'newZealand'
    ]);
  }

  isCompanyEuropean () : boolean {
    return this.companyService.country([
      'ireland',
      'northernIreland',
      'unitedKingdom'
    ]);
  }

  isLaborTimeEmpty ( time : HaynesProLaborTimeModel ) : boolean {
    return time.labour_time !== 0;
  }

  openDiagnosticTool ( vehicle : VehicleModel ) : Promise<any> {
    let variants : Array<HaynesProVehicleInfoModel>;

    return this.apiService.get('/vehicle', vehicle.id)
    .then(vehicle => this.getVehicleVariants(vehicle))
    .then(( vehicles : Array<HaynesProVehicleInfoModel> = [] ) => {
      variants = vehicles;

      if (vehicles.length === 1) {
        vehicle.haynes_pro_vehicle_id = vehicles[0].id;

        return this.saveVehicle(vehicle);
      }

      return vehicle.haynes_pro_vehicle_id
        ? Promise.resolve(vehicle)
        : this.selectVehicleVariant(vehicle, vehicles);
    })
    .then(( vehicle : VehicleModel ) => this.getVehicleTechnicalData(vehicle))
    .then(( technicalData : HaynesProTechnicalDataModel ) => {
      const modalRef = this.modalService.open(HaynesProDiagnosticToolComponent, {
        windowClass: 'full-modal'
      });

      modalRef.componentInstance.variants      = variants;
      modalRef.componentInstance.vehicle       = vehicle;
      modalRef.componentInstance.technicalData = technicalData;

      return modalRef.result;
    })
    .catch(err => this.messagesService.error(err));
  }

  purchaseLookups () : ng.IPromise<any> {
    return this.getRequestPrices()
    .then(( prices : IntegrationQuotePricesModel ) => {
      const modal = this.modalService.open(HaynesProPurchaseLookupsComponent);

      modal.componentInstance.prices = prices;

      return modal.result;
    });
  }

  removeGlobalVehicle ( vehicle : VehicleModel ) : Promise<VehicleModel> {
    vehicle.haynes_pro_model_id   = '';
    vehicle.haynes_pro_vehicle_id = '';

    return this.apiService.patch('/vehicle', vehicle, 'vehicle');
  }

  removeLaborOptionsWithNoLabor ( laborOptions : Array<HaynesProLaborOptionModel> ) : Array<HaynesProLaborOptionModel> {
    return laborOptions.filter(option => option.tasks.some(task => this.isLaborTimeEmpty(task)));
  }

  removeLaborTime ( laborTimes : Array<HaynesProLaborTimeModel>, time : HaynesProLaborTimeModel ) : void {
    const index = laborTimes.findIndex(item => ( time.name === item.name ) && ( time.subgroup_id === item.subgroup_id ));

    if ( index !== -1 ) {
      laborTimes[index].added = false;
    }
  }

  removeTimesWithNoLabor ( items : Array<any> ) : Array<any> {
    return items.filter(item => this.isLaborTimeEmpty(item));
  }

  saveLaborTimes ( type : HaynesProRecordTypeModel, recordId : string, vehicle : VehicleModel, times : Array<HaynesProLaborTimeModel> ) : Promise<Array<HaynesProLaborTimeModel>> {
    const url = type === 'event'
      ? '/events/add_item_from_haynes_pro'
      : '/invoices/add_item_from_haynes_pro';

    return this.apiService.post(url, {
      vehicle_id      : vehicle.id,
      haynes_pro_labor: {
        object_id: recordId,
        row      : times
      }
    });
  }

  saveSubscriptionSettings ( settings : CompanyIntegrationsModel ) : Promise<CompanyIntegrationsModel> {
    return this.apiService.patch('/company_integrations', settings, 'company_integration');
  }

  saveVehicle ( vehicle : VehicleModel ) : Promise<VehicleModel> {
    return this.apiService.patch('/vehicle', vehicle, 'vehicle');
  }

  searchSmartPack ( identifier : string, vehicle : VehicleModel, group : string, query : string ) : Promise<Array<HaynesProSmartPackCategoryModel>> {
    let params : any;
    let url    : string;

    switch (identifier) {
      case 'CASES':
        params = [ vehicle.id, group, query ];
        url    = '/haynes_pro/smartpack/filter_cases_systems';
        break;
      case 'RECALLS':
        params = [ vehicle.id, query ];
        url    = '/haynes_pro/smartpack/filter_recall_systems';
        break;
      case 'TSB':
        params = [ vehicle.id, group, query ];
        url    = '/haynes_pro/smartpack/filter_tsb_systems';
        break;
      default:
        params = [ vehicle.id, group, query ];
        url    = '/haynes_pro/smartpack/filter_tbs_cases_recalls_systems';
        break;
    }

    return this.apiService.get(url, params);
  }

  selectVehicleVariant ( vehicle : VehicleModel, vehicles ?: Array<HaynesProVehicleInfoModel> ) : Promise<VehicleModel> {
    return Promise.resolve()
    .then(() => vehicles
      ? Promise.resolve(vehicles)
      : this.getVehicleVariants(vehicle)
    )
    .then(( vehicles : Array<HaynesProVehicleInfoModel> ) => {
      const modal = this.modalService.open(HaynesProVehicleSelectionComponent, {
        size: 'xl'
      });

      modal.componentInstance.activeSide = !vehicles.length
        ? 'back'
        : 'front';

      modal.componentInstance.vehicle  = vehicle;
      modal.componentInstance.vehicles = vehicles;

      return modal.result;
    })
    .then(( vehicle : VehicleModel ) => this.saveVehicle(vehicle));
  }

  updateServiceIntervalsWithRemovedLaborTime ( intervals : Array<HaynesProServiceIntervalModel>, time : HaynesProLaborTimeModel ) : void {
    const index = intervals.findIndex(interval => ( interval.period_id === time.subgroup_id ) && ( interval.period_name === time.name ));

    if ( index !== -1 ) {
      intervals[index].added = false;
    }
  }

  updateSubscriptionStatus ( status : boolean ) : void {
    this._subscriptionStatus.next(status);
  }

}